import React from "react"
import Relay from "react-relay"
import Helmet from "react-helmet"
import Immutable from "immutable"
import { isEqual } from "lodash/lang"
import { get } from "lodash/object"
import Results from "../modules/Results"
import ResultsMap from "../modules/ResultsMap"
import State from "../../stores/State"
import { dispatch } from "../../Dispatcher"
import SearchFilters from "../modules/SearchFilters"
import SearchResults from "../modules/SearchResults"
import SearchControls from "../modules/SearchControls"
import { setArticleStyle } from "../../actions"
import { createSearchString } from "../../scripts/utils"
import { width } from "../../scripts/size"
import { injectIntl } from "react-intl"
import searchMessages from "../../messages/search"
import { getStyles, getStyleTag } from "../../styles/dynamicStyles"

import {
  FILTER_CONTROLS,
  DEFAULT_FILTER_JOIN,
  VALID_SORTS,
  MOBILE_ARTICLE_STYLE_ID,
  SEARCH_LOADING,
  SHOW_SEARCH_FILTERS
} from "../../constants"

import { setStateFromParams } from "../../actions"
import { decodeBreadcrumbsParameters } from "../../routes"
import api from "../../api"

let lastSearchUrl = "/search"

/**
 * local: A param defined in the route, which will be assigned to local state
 * store: The location of this value in the global store
 *
 * On construct we use this to init local state from params
 * On store update, we will use this to update local state from the global store
 *    and to update the query string based on global store values
 */
const ROUTE_PARAMS = [
  { local: "map", store: "searchMap" },
  { local: "perPage", store: "perPage" },
  { local: "articleStyle", store: "articleStyle" },
  { local: "q", store: "q" },
  { local: "sort", store: "sort" },
  { local: "tags", store: "searchTags" },
  { local: "terms", store: "queryTerms" },
  { local: "total", store: "pageSize" },
  { local: "types", store: "searchTypes" },
  { local: "bbox", store: "locationBBox" }
]

// We'll use this later to convert a key=value query string
const createQSKeyValue = (k, v) => {
  if (Immutable.Iterable.isIterable(v) || Array.isArray(v)) {
    return v.map(i => createQSKeyValue(k, i)).join("&")
  }
  return k + "=" + encodeURIComponent(v)
}

const initialState = State.getInitialState()

export function getLastSearchUrl() {
  return lastSearchUrl
}

class Search extends React.Component {
  constructor(props) {
    super(props)
    const globalState = this.getFromGlobalState()

    this.state = {
      ...ROUTE_PARAMS.reduce((state, { local }) => {
        if (local in props) {
          state[local] = props[local]
        }
        return state
      }, globalState),
      filterConfiguration: {}
    }
  }

  componentWillMount() {
    this.handleArticleStyles()
  }

  componentDidMount() {
    if (document.location.search) {
      this.getQueryString()
    } else {
      dispatch(setStateFromParams(this.props))
    }

    this.compareStateFromProps()
    this._stateListener = State.addListener(this.compareStateFromProps)

    document.body.classList.add("state-filters-initialised")
    if (document.body.getBoundingClientRect().width < this.props.autoShowAt) {
      dispatch({ type: SHOW_SEARCH_FILTERS, show: false })
    }
    window.addEventListener("resize", this.handleArticleStyles)

    api("GET", "fields/filter_configuration").then(filterConfiguration =>
      this.setState({ filterConfiguration })
    )
  }

  componentWillUnmount() {
    if (this._stateListener) {
      this._stateListener.remove()
    }
    window.removeEventListener("resize", this.handleArticleStyles)
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.map !== this.props.map) {
      dispatch(setStateFromParams(this.props))
    }
    if (prevState.doQuery !== this.state.doQuery && this.state.doQuery) {
      this.doQuery().then(() => {
        const queryString = this.getQueryString()
        if (document.location.search !== queryString) {
          history.replaceState(null, null, "/search" + queryString)
          lastSearchUrl = "/search" + queryString
        }
      })
    }
  }

  compareStateFromProps = () => {
    const newState = this.getFromGlobalState()

    if (!isEqual(newState, this.state)) {
      this.setState(newState)
    }
  }

  handleArticleStyles = () => {
    if (width() <= 512) {
      this.setState({ articleStyle: MOBILE_ARTICLE_STYLE_ID })
      dispatch(setArticleStyle(MOBILE_ARTICLE_STYLE_ID))
    }
  }

  getQueryString = () => {
    /**
     * Now we will update our url to match the new state.
     */
    const filters = State.get("filters").toJS()
    const breadcrumbs = State.get("breadcrumbs").toJS()
    const dates = State.get("dates").toJS()

    this.props.relay.setVariables({ filters, dates, breadcrumbs }, () => {
      this.setState({ filters, dates, breadcrumbs })
    })

    const baseQuery = ROUTE_PARAMS.reduce((query, { local, store }) => {
      // Pull values from our State object, we will add these to the query string.
      const value = Array.isArray(store) ? State.getIn(store) : State.get(store)
      const initialValue = Array.isArray(store)
        ? initialState.getIn(store)
        : initialState.get(store)
      if (value !== initialValue) {
        const qsValue = createQSKeyValue(local, value)
        if (qsValue) {
          query.push(qsValue)
        }
      }
      return query
    }, [])

    // We need to manually add filters, breadcrumbs and dates to the query string
    const customQuery = []
    for (const [key, value] of Object.entries(filters)) {
      if (value.length === 0) {
        continue
      }

      const qsValue = createQSKeyValue(key, value)
      if (qsValue) {
        customQuery.push(qsValue)
      }
    }
    for (const [key, value] of Object.entries(breadcrumbs)) {
      if (value.length === 0) {
        continue
      }

      const qsValue = createQSKeyValue(key, value)
      if (qsValue) {
        customQuery.push(qsValue)
      }
    }
    for (const [key, value] of Object.entries(dates)) {
      const dateFrom = value.from
      const dateTo = value.to

      const qsValueFrom = dateFrom
        ? createQSKeyValue(`${key}From`, dateFrom)
        : null
      if (qsValueFrom) {
        customQuery.push(qsValueFrom)
      }

      const qsValueTo = dateTo ? createQSKeyValue(`${key}To`, dateTo) : null
      if (qsValueTo) {
        customQuery.push(qsValueTo)
      }
    }

    const query = [...baseQuery, ...customQuery]

    return query.length ? "?" + query.join("&") : ""
  }

  getFromGlobalState = () => {
    return {
      doQuery: State.get("doQuery"),
      filterControls: State.get("filterControls").toJS(),
      filters: State.get("filters").toJS(),
      breadcrumbs: State.get("breadcrumbs").toJS(),
      dates: State.get("dates").toJS(),
      total: State.get("pageSize"),
      perPage: State.get("perPage"),
      q: State.get("q"),
      reflowResults: State.get("reflowResults"),
      showSearchFilters: State.get("showSearchFilters"),
      articleStyle: State.get("articleStyle"),
      sort: State.get("sort"),
      tags: State.get("searchTags").toJS(),
      terms: State.get("queryTerms").toJS(),
      types: State.get("searchTypes").toJS(),
      bbox: State.get("locationBBox"),
      map: State.get("searchMap")
    }
  }

  doQuery = () => {
    return new Promise((resolve, reject) => {
      this.props.relay.setVariables(this.state, readyState => {
        dispatch({ type: SEARCH_LOADING, loading: !readyState.done })

        if (readyState.done) {
          resolve()
        } else if (readyState.error) {
          reject(readyState.error)
        }
      })
    })
  }

  render() {
    const {
      showSearchFilters,
      articleStyle,
      perPage,
      map,
      q,
      types,
      terms,
      tags,
      filterControls,
      filters,
      breadcrumbs,
      dates
    } = this.state

    const { intl, viewer } = this.props

    const facets = get(viewer, ["resources", "facets"])
    const total = get(viewer, ["resources", "total"])
    return (
      <main id="main" role="main" tabIndex={0}>
        {getStyleTag(["search-results"])}
        <Helmet title={intl.formatMessage(searchMessages.explore)} />
        <div className="search-results" style={getStyles("search-results")}>
          <SearchControls
            articleStyle={articleStyle}
            showSearchFilters={showSearchFilters}
            q={q}
            types={types}
            licenses={filters.license}
            terms={terms}
            tags={tags}
            filterControls={filterControls}
            filters={filters}
            dates={dates}
            facets={facets}
            total={total}
            hideDisplayTools={map}
            breadcrumbsSearch={breadcrumbs}
            filterConfiguration={this.state.filterConfiguration}
          />
          <SearchResults
            viewer={viewer}
            articleStyle={articleStyle}
            perPage={perPage}
            showSearchFilters={showSearchFilters}
            map={map}
          />
        </div>
      </main>
    )
  }
}

function prepareControlValue(value, control) {
  return control.prepareValue ? control.prepareValue(value) : `"${value}"`
}

export default Relay.createContainer(injectIntl(Search), {
  // If you want a specific field, just specify it above or you can add it in the FILTER_CONTROLS in constants.js and concat it here
  initialVariables: {
    creator: null,
    language: null,
    title: null,
    first: null,
    direction: null,
    q: "",
    terms: null,
    types: null,
    custom_fields: "{}",
    tags: null,
    sort: null,
    total: null,
    map: false,
    location_bbox: null,
    bbox: null,
    breadcrumbsSearch: null,
    filters: {},
    dates: {}
  },
  prepareVariables: prevVariables => {
    const {
      q,
      terms,
      types,
      tags,
      sort,
      total,
      map,
      bbox,
      filters,
      breadcrumbs,
      dates
    } = prevVariables
    const sortValues = VALID_SORTS.map(s => s.v)

    // This value is applied to first: or last: depending on sort
    const num = total || State.get("pageSize")
    const [sortField, last = ""] = (sortValues.indexOf(sort) > -1 || !!q.length
      ? sort || (q && "relevance")
      : State.get("defaultSort")
    ).split(":")

    const typeQuery = types && types.length ? types.join(" OR ") : null

    const variables = {
      ...prevVariables,
      q: createSearchString(q, terms) || "",
      types:
        (typeQuery ? "( " + typeQuery + " ) " : "") +
        "NOT cms_page AND NOT notification AND NOT onboarding_step", // Need to exclude CMS pages, notifications and onboarding steps from results
      custom_fields: JSON.stringify({}),
      tags: tags && tags.length > 0 ? [tags] : null,
      sort: sortField,
      direction: last ? "asc" : "desc",
      first: num,
      geocoded: bbox || map ? true : false,
      breadcrumbsSearch: null
    }

    // dynamically adding and preparing breadcrumbs to variable
    if (breadcrumbs) {
      let breadcrumbsSearchObj = {}

      for (const [, value] of Object.entries(breadcrumbs)) {
        const valueDecoded = value
          ? Object.entries(decodeBreadcrumbsParameters(value)).reduce(
              (accumulator, [key, value]) => ({
                ...accumulator,
                [key]: value.map(v => v.key)
              }),
              {}
            )
          : {}

        breadcrumbsSearchObj = valueDecoded
          ? { ...breadcrumbsSearchObj, ...valueDecoded }
          : { ...breadcrumbsSearchObj }
      }

      const breadcrumbsSearchStr = JSON.stringify(breadcrumbsSearchObj)
      if (breadcrumbsSearchStr !== "{}") {
        variables.breadcrumbsSearch = breadcrumbsSearchStr
      }
    }

    // dynamically adding and preparing dates to custom_fields
    if (dates) {
      const existedCustomFields = JSON.parse(variables.custom_fields)

      for (const [key, value] of Object.entries(dates)) {
        const dateFromKey = `${key}_gt`
        const dateToKey = `${key}_lt`

        const dateFromValue = value.from
        const dateToValue = value.to

        if (dateFromValue) {
          existedCustomFields[dateFromKey] = dateFromValue
        }
        if (dateToValue) {
          existedCustomFields[dateToKey] = dateToValue
        }
      }

      variables.custom_fields = JSON.stringify(existedCustomFields)
    }

    // dynamically adding and preparing filters to custom_fields or variables
    if (filters) {
      const existedCustomFields = JSON.parse(variables.custom_fields)

      for (const [key, value] of Object.entries(filters)) {
        const control = FILTER_CONTROLS.find(control => control.name === key)
        const queryValue = control
          ? value
              .map(value => prepareControlValue(value, control))
              .join(` ${control.join || DEFAULT_FILTER_JOIN} `)
          : value && value.length && value.join(" OR ")

        if (control && control.isDefault) {
          variables[key] = queryValue
        } else {
          existedCustomFields[key] = queryValue || undefined
        }
      }

      variables.custom_fields = JSON.stringify(existedCustomFields)
    }

    return variables
  },
  fragments: {
    viewer: () => Relay.QL`
        fragment on Viewer {
            resources(
              first: $first
              direction: $direction
              q: $q
              type: $types
              custom_fields: $custom_fields
              tag_search: $tags
              order: $sort
              access_rights: "published"
              orphan: true
              geocoded: $geocoded
              location_bbox: $location_bbox
              breadcrumbs_search: $breadcrumbsSearch
              # Clunk, have to manually add these, should match FILTER_CONTROLS in constants.js
              creator: $creator
              title: $title
              language: $language
            )
            {
              total
              facets {
                ${SearchFilters.getFragment("facets")}
              }
              edges {
                cursor
                ${Results.getFragment("resources")}
                node { id }
              }
              exact_locations(first: 1000) {
                edges {
                  cursor
                  ${ResultsMap.getFragment("exactLocations")}
                  node { count }
                }
              }
              pageInfo {
                hasNextPage
              }
            }
        }
    	`
  }
})



// WEBPACK FOOTER //
// ./webapp/components/pages/Search.js